import React, { useState, useEffect, useCallback } from 'react';
import { Quote } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import type { CarouselApi } from '@/components/ui/carousel';

const testimonials = [
  {
    name: 'Ailin Werner',
    title: 'Head of AI at YOUNG',
    quote: 'Antonio combines deep Salesforce expertise with one of the most positive and determined attitudes I\'ve ever seen. His resilience, professionalism, and ability to stay motivated inspired the entire team while consistently delivering outstanding results.',
  },
  {
    name: 'Pavel Osadchiy',
    title: 'HTML Email Developer',
    quote: 'Antonio is an excellent team player with a strong technical background in email development and CRM platforms. He consistently delivers high quality work, pays close attention to detail, and is always willing to support his colleagues.',
  },
  {
    name: 'Ondřej Liška',
    title: 'Czech Site Lead',
    quote: 'Antonio consistently delivered high quality HTML email development across multiple markets with impressive speed. His technical expertise and willingness to help teammates made him an invaluable member of the team.',
  },
  {
    name: 'Christine Yang',
    title: 'Marketing Automation & CRM Specialist',
    quote: 'Antonio is detail oriented, highly knowledgeable, and understands exactly what marketers need. He consistently delivers excellent work quickly and is a pleasure to collaborate with.',
  },
  {
    name: 'Tereza Diaz',
    title: 'CRM Data & Campaign Planning Specialist',
    quote: 'Antonio demonstrated exceptional technical expertise and professionalism. His HTML, CSS, and email development skills are outstanding, and he consistently delivers clean, reliable code under tight deadlines.',
  },
  {
    name: 'Bo Ament',
    title: 'CRM Strategist',
    quote: 'Antonio is proactive, detail oriented, and always thinks ahead. His coding skills, automation knowledge, and willingness to help make him someone I would happily work with again.',
  },
  {
    name: 'Noa Breed',
    title: 'Performance Creative Strategist | LABAREAU',
    quote: 'Antonio managed our CRM with great care, responded quickly, and consistently brought new ideas to improve our email marketing. His proactive mindset had a real impact on our team\'s success.',
  },
  {
    name: 'Humperdinck Jackman',
    title: 'CEO | ESG Pro Group',
    quote: 'Without a doubt, Antonio is the best Google Ads consultant we have worked with. His strategic thinking, research, and execution consistently delivered outstanding quality.',
  },
  {
    name: 'Lelle Peterson',
    title: 'Account Director | DEPT®',
    quote: 'Antonio\'s developer expertise and outstanding attitude made him an invaluable member of our team. His dedication and consistently high standards are truly exceptional.',
  },
  {
    name: 'Marius Manu',
    title: 'Field Services Engineer',
    quote: 'Antonio consistently demonstrates exceptional technical expertise, problem solving skills, and professionalism. He delivers outstanding results while communicating effectively and collaborating with everyone around him.',
  },
  {
    name: 'Bram van der Doelen',
    title: 'CRM Solution Architect | DEPT®',
    quote: 'Antonio is always ready to help and willing to go the extra mile. His knowledge of Email HTML and CSS is extraordinary, making him a valuable part of our Marketing Automation team.',
  },
  {
    name: 'Joshua Mendoza',
    title: 'Senior CRM Developer',
    quote: 'Antonio quickly understands complex challenges, solves problems efficiently, and creates a positive environment for everyone around him. His passion for learning and continuous improvement makes him an exceptional teammate.',
  },
  {
    name: 'Kalin Markovic',
    title: 'Digital Media Manager',
    quote: 'Antonio is dedicated, adaptable, and consistently delivers high quality work under pressure. He approaches complex problems with practical solutions while maintaining exceptional quality standards.',
  },
  {
    name: 'David Gagaleski',
    title: 'Salesforce Administrator | Valtech',
    quote: 'Antonio combines strong technical expertise with outstanding communication skills. He explains complex concepts clearly, collaborates effortlessly, and is constantly striving to improve both personally and professionally.',
  },
  {
    name: 'Daan Kramer',
    title: 'Competence Lead Marketing Automation Tech',
    quote: 'Antonio is one of the hardest working colleagues I\'ve ever worked with. His rapid professional growth and the quality of his work are truly remarkable.',
  },
  {
    name: 'Andrej Stefanovski',
    title: 'Social Media Marketing Consultant',
    quote: 'From planning to execution, Antonio consistently impressed me with his ability to adapt quickly, think strategically, and deliver excellent results across every stage of a project.',
  },
  {
    name: 'Sanja Jovanović',
    title: 'Email Developer',
    quote: 'Antonio is accurate, professional, and always eager to learn and improve. His positive attitude, clean code, and reliability make him someone you genuinely enjoy working with.',
  },
];

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TestimonialsCarousel = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const plugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })
  );

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  return (
    <section className="bg-background overflow-hidden py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-secondary">
          What Colleagues & <span className="text-primary">Clients</span> Say
        </h2>

        <Carousel
          setApi={setApi}
          plugins={[plugin.current]}
          opts={{ align: 'start', loop: true }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {testimonials.map((t, i) => (
              <CarouselItem key={i} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                <div className="h-full p-6 md:p-8 rounded-xl border border-border bg-card shadow-sm flex flex-col">
                  <Quote className="w-8 h-8 text-primary/40 mb-4 shrink-0" />
                  <p className="text-card-foreground italic leading-relaxed mb-6 flex-grow text-base">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                    <div className="min-w-0">
                      <p className="font-semibold text-secondary truncate">{t.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{t.title}</p>
                    </div>
                    <LinkedInIcon className="w-5 h-5 text-secondary shrink-0 ml-3" />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="flex top-1/2 -translate-y-1/2 left-2 md:-left-4 lg:-left-12" />
          <CarouselNext className="flex top-1/2 -translate-y-1/2 right-2 md:-right-4 lg:-right-12" />
        </Carousel>

        <div className="flex justify-center flex-wrap gap-2 mt-6">
          {Array.from({ length: count }).map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                current === index
                  ? 'bg-primary'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
